import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SiretLookup from './SiretLookup';
import './ClientModal.css';

function ClientModal({ client, onClose, onSave, isAdmin, currentUser }) {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    entreprise: '',
    adresse: '',
    numero_siret: '',
    montant_bon_commande: '',
    temps_engagement: '',
    prix_mensualite: '',
    nombre_paiements: '1',
    client_signe: false,
    paye: false,
    montant_paye: '',
    agent_id: '',
    commercial_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agents, setAgents] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);

  // Désactiver le formulaire si le client a signé et que l'utilisateur n'est pas admin
  const isFormDisabled = client && client.client_signe && !isAdmin;

  useEffect(() => {
    if (client) {
      setFormData({
        nom: client.nom || '',
        prenom: client.prenom || '',
        email: client.email || '',
        telephone: client.telephone || '',
        entreprise: client.entreprise || '',
        adresse: client.adresse || '',
        numero_siret: client.numero_siret || '',
        montant_bon_commande: client.montant_bon_commande || '',
        temps_engagement: client.temps_engagement || '',
        prix_mensualite: client.prix_mensualite || '',
        nombre_paiements: client.nombre_paiements || '1',
        client_signe: client.client_signe || false,
        paye: client.paye || false,
        montant_paye: client.montant_paye || '',
        agent_id: client.agent_id || '',
        commercial_id: client.commercial_id || ''
      });
    }

    // Charger la liste des utilisateurs assignables si admin
    if (isAdmin) {
      fetchAssignableUsers();
    }
  }, [client, isAdmin]);

  const fetchAssignableUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/users/assignable', {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Séparer les agents et les commerciaux (superviseur_agent et chef_equipe comptent comme agent)
      const agentsList = response.data.filter(user => user.role === 'agent' || user.role === 'superviseur_agent' || user.role === 'chef_equipe');
      const commerciauxList = response.data.filter(user => user.role === 'commercial');
      setAgents(agentsList);
      setCommerciaux(commerciauxList);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await onSave(formData);
    } catch (err) {
      setError(err.response?.data?.error || '⚙️ Système en maintenance, veuillez réessayer plus tard');
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{client ? 'Modifier le client' : 'Nouveau client'}</h2>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          {isFormDisabled && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffc107',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              color: '#856404',
              fontSize: '14px'
            }}>
              🔒 <strong>Client avec signature électronique validée</strong><br />
              Les informations ne peuvent plus être modifiées. Contactez un administrateur si nécessaire.
            </div>
          )}
          {!isFormDisabled && (
            <SiretLookup
              initialSiret={formData.numero_siret}
              onCompanyFound={(data) => {
                setFormData(prev => ({
                  ...prev,
                  nom: data.dirigeant_nom || prev.nom,
                  entreprise: data.entreprise || prev.entreprise,
                  adresse: data.adresse || prev.adresse,
                  numero_siret: data.numero_siret || prev.numero_siret
                }));
              }}
            />
          )}
          <div className="form-group">
            <label htmlFor="prenom">Prénom *</label>
            <input
              type="text"
              id="prenom"
              name="prenom"
              value={formData.prenom}
              onChange={handleChange}
              placeholder="Jean"
              required
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="nom">Nom *</label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              placeholder="Dupont"
              required
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="jean.dupont@exemple.com"
              required
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="telephone">Téléphone *</label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              placeholder="+33 6 12 34 56 78"
              required
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="entreprise">Entreprise</label>
            <input
              type="text"
              id="entreprise"
              name="entreprise"
              value={formData.entreprise}
              onChange={handleChange}
              placeholder="Entreprise SARL"
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="adresse">Adresse</label>
            <textarea
              id="adresse"
              name="adresse"
              value={formData.adresse}
              onChange={handleChange}
              placeholder="123 Rue de la Paix, 75001 Paris"
              rows="3"
              disabled={isFormDisabled}
            ></textarea>
          </div>
          <div className="form-group">
            <label htmlFor="numero_siret">Numéro SIRET</label>
            <input
              type="text"
              id="numero_siret"
              name="numero_siret"
              value={formData.numero_siret}
              onChange={handleChange}
              placeholder="12345678901234"
              maxLength="14"
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="montant_bon_commande">Montant bon de commande (€)</label>
              <input
                type="number"
                id="montant_bon_commande"
                name="montant_bon_commande"
                value={formData.montant_bon_commande}
                onChange={handleChange}
                placeholder="15000.00"
                step="0.01"
                min="0"
                disabled={isFormDisabled}
              />
            </div>
            <div className="form-group">
              <label htmlFor="temps_engagement">Temps d'engagement (mois)</label>
              <input
                type="number"
                id="temps_engagement"
                name="temps_engagement"
                value={formData.temps_engagement}
                onChange={handleChange}
                placeholder="12"
                min="1"
                disabled={isFormDisabled}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="prix_mensualite">Prix mensualité (€)</label>
            <input
              type="number"
              id="prix_mensualite"
              name="prix_mensualite"
              value={formData.prix_mensualite}
              onChange={handleChange}
              placeholder="1250.00"
              step="0.01"
              min="0"
              disabled={isFormDisabled}
            />
          </div>
          <div className="form-group">
            <label htmlFor="nombre_paiements">Paiement en</label>
            <select
              id="nombre_paiements"
              name="nombre_paiements"
              value={formData.nombre_paiements}
              onChange={handleChange}
              disabled={isFormDisabled}
            >
              <option value="1">1 fois</option>
              <option value="2">2 fois</option>
              <option value="3">3 fois</option>
            </select>
          </div>
          <div className="form-row">
            <div className="form-group-checkbox">
              <input
                type="checkbox"
                id="client_signe"
                name="client_signe"
                checked={formData.client_signe}
                onChange={handleChange}
                disabled={!isAdmin || (client && client.client_signe)}
              />
              <label htmlFor="client_signe">
                Client signé {!isAdmin && '(Admin uniquement)'}
                {client && client.client_signe && ' ✓ Signature validée'}
              </label>
            </div>
            <div className="form-group-checkbox">
              <input
                type="checkbox"
                id="paye"
                name="paye"
                checked={formData.paye}
                onChange={handleChange}
                disabled={!isAdmin}
              />
              <label htmlFor="paye">Payé {!isAdmin && '(Admin uniquement)'}</label>
            </div>
          </div>
          {formData.paye && (
            <div className="form-group">
              <label htmlFor="montant_paye">Montant payé (€)</label>
              <input
                type="number"
                id="montant_paye"
                name="montant_paye"
                value={formData.montant_paye}
                onChange={handleChange}
                placeholder="Montant réellement payé"
                step="0.01"
                min="0"
                disabled={!isAdmin}
              />
            </div>
          )}
          {isAdmin && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="agent_id">Agent Téléphonique</label>
                <select
                  id="agent_id"
                  name="agent_id"
                  value={formData.agent_id}
                  onChange={handleChange}
                >
                  <option value="">Aucun</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.nom}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="commercial_id">Commercial</label>
                <select
                  id="commercial_id"
                  name="commercial_id"
                  value={formData.commercial_id}
                  onChange={handleChange}
                >
                  <option value="">Aucun</option>
                  {commerciaux.map(commercial => (
                    <option key={commercial.id} value={commercial.id}>
                      {commercial.nom}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              {isFormDisabled ? 'Fermer' : 'Annuler'}
            </button>
            {!isFormDisabled && (
              <button type="submit" className="btn-save" disabled={loading}>
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClientModal;
